import { useRef, useState } from 'react';

import { useEnvironment } from 'context/environment';
import { createPresigned } from 'services/api/uploads';

import Upload, { UploadState } from './Upload';

type UseDirectUploadOptions = {
  defaultDocType?: string;
};

export default function useDirectUpload(initialState: Upload[] = [], options?: UseDirectUploadOptions) {
  const [uploads, setUploads] = useState<Upload[]>(initialState);
  const [uploading, setUploading] = useState(false);
  const sequentialId = useRef(Math.max(...uploads.map((x) => +x.id), 0) + 1);
  const { environment } = useEnvironment();
  const { defaultDocType } = options || {};

  const updateFiles = (upload: Upload) => {
    setUploads((uploads) => uploads.map((x) => (x.id != upload.id ? x : new Upload(upload))));
  };

  const uploadFiles = async (files: File[]) => {
    if (uploading) return null;

    const newUploads = files.map(
      (x) =>
        new Upload({
          id: '' + sequentialId.current++,
          file: x,
          uploadDomain: environment?.OLIO_FILES_DOMAIN,
          docType: defaultDocType,
        })
    );

    setUploads((prevUploads) => [...prevUploads, ...newUploads]);

    setUploading(true);

    try {
      const results = await Promise.all(
        newUploads.map((upload) =>
          upload.start(createPresigned, updateFiles).catch((error) => {
            // If presign or upload fails, mark the upload as errored
            upload.status = UploadState.error;
            upload.error = error;
            updateFiles(upload);
          })
        )
      );
      return results;
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = (id: string) => {
    setUploads((prevUploads) => prevUploads.filter((x) => x.id != id));
  };

  const setDocType = (id: string, docType: string | null) => {
    const upload = uploads.find((x) => x.id == id)!;
    upload.docType = docType ?? undefined;
    updateFiles(upload);
  };

  const resetUploads = () => {
    setUploads([]);
  };

  return {
    removeUpload,
    setDocType,
    uploadFiles,
    resetUploads,
    setUploads,
    uploads,
    uploading,
  };
}
